<script setup>
  import { computed, nextTick, onMounted, ref, watch } from "vue"
  import Swal from "sweetalert2"
  import HabitacionesForm from "@/components/cotizador/HabitacionesForm.vue"
  import ResultadoVuelos from "@/components/cotizador/ResultadoVuelos.vue"
  import VueloManualModal from "@/components/cotizador/VueloManualModal.vue"
  import FechaDatePicker from "@/components/cotizador/FechaDatePicker.vue"

  import { useFechas } from "@/composables/useFechas"
  import { useVuelos } from "@/composables/useVuelos"
  import { useHabitaciones } from "@/composables/useHabitaciones"
  import { useVueloManual } from "@/composables/useVueloManual"

  import { useCotizadorState } from "@/composables/useCotizadorState"
  import { useCotizadorComputed } from "@/composables/useCotizadorComputed"
  import { useCotizadorWatchers } from "@/composables/useCotizadorWatchers"
  import { useCotizadorActions } from "@/composables/useCotizadorActions"
  import { configDestinos } from "@/utils/destinosConfig"
  import { destinosDisponibles } from "@/utils/destinos"
  import { es } from "date-fns/locale"

  const props = defineProps({
    // true cuando la ventana está visible (no minimizada)
    activa: {
      type: Boolean,
      default: true
    },
    // sesión persistida a restaurar: { datos, job, estado }
    sesion: {
      type: Object,
      default: null
    }
  })

  const emit = defineEmits([
    "update:estado",
    "update:resumen",
    "update:job",
    "update:datos"
  ])

  // =========================
  // ALERTAS POR VENTANA
  // =========================

  const alertasPendientes = []

  function notificar(opts) {
    if (props.activa) {
      return Swal.fire(opts)
    }
    alertasPendientes.push(opts)
  }

  watch(() => props.activa, async (visible) => {
    if (!visible) return
    while (alertasPendientes.length) {
      await Swal.fire(alertasPendientes.shift())
    }
  })

  function onJob(job) {
    emit("update:job", job)
  }

  // =========================
  // STATE
  // =========================

  const cotizacionGenerada = ref(false)
  const state = useCotizadorState()
  const porcentajeHoteles = ref(0)
  const porcentajeVuelos = ref(0)
  const precioTraslados = ref(0)

  const {
    sinVuelo,
    soloDirectos,
    destino,
    origen,
    telefono,
    visa,
    modalVueloVisible
  } = state

  const {
    fechaIda,
    fechaRegreso,
    dias,
    dias1,
    dias2,
    bloqueoRegreso,
    minFecha
  } = useFechas(destino)

  const minFechaRegreso = computed(() => {

    if (!fechaIda.value) {
      return minFecha
    }

    const fecha = new Date(fechaIda.value)

    fecha.setDate(fecha.getDate() + 1)

    return fecha

  })

  const vuelos = useVuelos({ notificar, onJob })

  const {
    buscarVuelos,
    reanudarBusqueda,
    generarCotizacionCorp,
    reanudarCotizacion,
    resultados,
    loading,
    searchId
  } = vuelos

  const habitacionesState = useHabitaciones()

  const {
    habitaciones,
    agregarHabitacion,
    eliminarHabitacion,
    validarPasajeros,
    totales,
    setAdultos,
    setNinos,
    setInfantes,
    totalPersonas
  } = habitacionesState

  const vueloManual = useVueloManual()

  const {
    guardarVuelo,

    multiDestino,

    ida,
    intermedio,
    regreso,

    precioSimple,
    preciosMulti
  } = vueloManual

  const computedState =
    useCotizadorComputed(
      destino,
      habitaciones
    )

  const hayResultados = computed(() => {

    const vuelos = resultados.value?.vuelos

    if (!vuelos) {
      return false
    }

    // SIMPLE
    if (vuelos.ida && vuelos.regreso) {
      return true
    }

    // MULTIDESTINO
    if (
      Array.isArray(vuelos.resultados) &&
      vuelos.resultados.length
    ) {
      return true
    }

    // DECAMERON
    if (
      Array.isArray(vuelos) &&
      vuelos.length
    ) {
      return true
    }

    return false

  })

  const cotizando = ref(false)

  const {

    showVisa,

    telefonoClass,

    destinoClass,

    showDiasSimple,

    showDiasCombinado,

    opcionesDias,

    labelsCombinado,

    minDias1,

    minDias2,

    colClass

  } = computedState

  useCotizadorWatchers({
    destino,
    dias,
    dias1,
    dias2,
    sinVuelo
  })

  const actions =
    useCotizadorActions({
      sinVuelo,
      origen,
      destino,
      fechaIda,
      fechaRegreso,
      habitaciones,
      telefono,
      dias,
      dias1,
      dias2,
      soloDirectos,
      visa,
      buscarVuelos,
      validarPasajeros,
      totales,
      resultados,
      loading,
      generarCotizacionCorp,
      searchId,
      guardarVuelo,
      modalVueloVisible,
      porcentajeHoteles,
      porcentajeVuelos,
      precioTraslados,
      notificar
    })

  const {
    handleBuscar,
    handleCotizarCorp,
    handleGuardarVuelo,
    handleAbrirVueloManual
  } = actions

  // =========================
  // ESTADO PARA EL GESTOR DE VENTANAS
  // =========================

  const estadoBase = ref("editando")

  let huboProceso = false

  watch(loading, (v) => {
    if (v) huboProceso = true
  })

  const estadoVentana = computed(() => {
    if (cotizando.value) return "cotizando"
    if (loading.value) return "buscando"
    if (cotizacionGenerada.value) return "completada"
    return estadoBase.value
  })

  watch(estadoVentana, (v) => {
    emit("update:estado", v)
  }, { immediate: true })

  // =========================
  // PERSISTENCIA DE LA SESIÓN
  // =========================

  function snapshotDatos() {
    return {
      sinVuelo: sinVuelo.value,
      soloDirectos: soloDirectos.value,
      destino: destino.value,
      origen: origen.value,
      telefono: telefono.value,
      visa: visa.value,
      porcentajeHoteles: porcentajeHoteles.value,
      porcentajeVuelos: porcentajeVuelos.value,
      precioTraslados: precioTraslados.value,
      fechaIda: fechaIda.value,
      fechaRegreso: fechaRegreso.value,
      dias: dias.value,
      dias1: dias1.value,
      dias2: dias2.value,
      habitaciones: JSON.parse(JSON.stringify(habitaciones.value)),
      searchId: searchId.value,
      resultados: JSON.parse(JSON.stringify(resultados.value))
    }
  }

  watch(
    [
      sinVuelo, soloDirectos, destino, origen, telefono, visa,
      porcentajeHoteles, porcentajeVuelos, precioTraslados,
      fechaIda, fechaRegreso, dias, dias1, dias2,
      habitaciones, searchId, resultados
    ],
    () => emit("update:datos", snapshotDatos()),
    { deep: true, immediate: true }
  )

  async function restaurarSesion() {

    const d = props.sesion?.datos

    if (!d) return

    sinVuelo.value = !!d.sinVuelo
    soloDirectos.value = !!d.soloDirectos
    destino.value = d.destino || "PUJ"
    origen.value = d.origen || "MDE"
    telefono.value = d.telefono || ""
    visa.value = d.visa || "sin_visa"
    porcentajeHoteles.value = d.porcentajeHoteles || 0
    porcentajeVuelos.value = d.porcentajeVuelos || 0
    precioTraslados.value = d.precioTraslados || 0

    if (Array.isArray(d.habitaciones) && d.habitaciones.length) {
      habitaciones.value = JSON.parse(JSON.stringify(d.habitaciones))
    }

    searchId.value = d.searchId || null

    if (d.resultados) {
      resultados.value = JSON.parse(JSON.stringify(d.resultados))
    }

    // esperar a que corran los watchers de destino (resetean días)
    await nextTick()

    dias.value = d.dias
    dias1.value = d.dias1
    dias2.value = d.dias2
    fechaIda.value = d.fechaIda

    // esperar al watcher que recalcula fechaRegreso desde fechaIda
    await nextTick()

    fechaRegreso.value = d.fechaRegreso

    // esperar al watcher que resetea cotizacionGenerada al cambiar fechas
    await nextTick()

    cotizacionGenerada.value = props.sesion.estado === "completada"
    estadoBase.value = props.sesion.estado === "error" ? "error" : "editando"
  }

  onMounted(async () => {

    if (!props.sesion) return

    await restaurarSesion()

    const job = props.sesion.job

    if (!job?.jobId) return

    // retomar el proceso que quedó corriendo antes de recargar
    if (job.tipo === "buscar") {

      // corp no encadena cotización automática: el usuario define
      // porcentajes y genera manualmente
      const res = await reanudarBusqueda(job.jobId)

      if (!res) estadoBase.value = "error"

    } else if (job.tipo === "cotizar") {

      cotizando.value = true

      try {
        const ok = await reanudarCotizacion(job.jobId)
        if (ok) cotizacionGenerada.value = true
        else estadoBase.value = "error"
      } finally {
        cotizando.value = false
      }

    }

  })

  // =========================
  // LÓGICA CORP
  // =========================

  const origenesPermitidosJPN = ["MDE", "BOG", "CLO", "CTG"]

  const origenesDisponibles = computed(() => {

    const todos = [
      { value: "BOG", label: "Bogotá" },
      { value: "CLO", label: "Cali" },
      { value: "MDE", label: "Medellín" },
      { value: "CTG", label: "Cartagena" },
      { value: "BAQ", label: "Barranquilla" },
      { value: "BGA", label: "Bucaramanga" },
      { value: "LET", label: "Leticia" },
      { value: "AXM", label: "Armenia" },
      { value: "PEI", label: "Pereira" },
      { value: "PSO", label: "Pasto" },
      { value: "PPN", label: "Popayán" },
      { value: "SMR", label: "Santa Marta" },
      { value: "MTR", label: "Montería" },
      { value: "RCH", label: "Riohacha" },
      { value: "MVP", label: "Mitú" },
      { value: "IBE", label: "Ibagué" },
      { value: "NVA", label: "Neiva" },
      { value: "CUC", label: "Cúcuta" },
      { value: "MZL", label: "Manizales" },
      { value: "VUP", label: "Valledupar" }
    ]

    // Si destino contiene JPN
    if (destino.value?.includes("JPN")) {

      return todos.filter(o =>
        origenesPermitidosJPN.includes(o.value)
      )

    }

    return todos

  })

  watch(destino, () => {

    if (
      destino.value?.includes("JPN") &&
      !origenesPermitidosJPN.includes(origen.value)
    ) {

      origen.value = "MDE"

    }

  })

  watch(destino, (nuevoDestino) => {

    const config = configDestinos[nuevoDestino]

    if (
      config?.tipo === "simple" &&
      config.opciones?.length
    ) {

      dias.value = config.opciones[0]

    }

  }, { immediate: true })

  watch([fechaIda, fechaRegreso, destino, origen], () => {
    cotizacionGenerada.value = false
    estadoBase.value = "editando"
  })

  watch([origen, destino], () => {
    emit(
      "update:resumen",
      `${origen.value || "?"} → ${destino.value || "?"}`
    )
  }, { immediate: true })

  const destinosOcultos = ["DECAMERON"]

  const destinosFiltrados = computed(() =>
    destinosDisponibles.filter(
      d => !destinosOcultos.includes(d.value)
    )
  )

  const destinosNacionales = [
    "SMR",
    "CTG",
    "ADZ"
  ]

  const mostrarPrecioTraslados = computed(() =>
    destinosNacionales.includes(destino.value)
  )

  const precioTrasladosFormateado = computed({
    get() {
      return precioTraslados.value
        ? new Intl.NumberFormat("es-CO").format(precioTraslados.value)
        : ""
    },
    set(value) {
      precioTraslados.value = Number(
        value.replace(/\./g, "").replace(/,/g, "")
      ) || 0
    }
  })

  // =========================
  // HANDLERS
  // =========================

  async function onBuscar() {
    cotizacionGenerada.value = false
    estadoBase.value = "editando"
    huboProceso = false

    await handleBuscar()

    // corp no encadena cotización: éxito = hay resultados para trabajar
    if (huboProceso && !hayResultados.value) {
      estadoBase.value = "error"
    }
  }

  async function onCotizar() {
    cotizando.value = true
    try {
      const ok = await handleCotizarCorp()
      if (ok) cotizacionGenerada.value = true
      else estadoBase.value = "error"
    } finally {
      cotizando.value = false
    }
  }
</script>

<template>

  <div class="container py-2" style="background-color: #f8f9fa; border-radius: 6px;">

    <!-- TOGGLES -->
    <div class="d-flex justify-content-center gap-4 flex-wrap mb-4">

      <!-- SIN VUELO -->
      <div class="form-check form-switch">

        <input class="form-check-input" type="checkbox" v-model="sinVuelo">

        <label class="form-check-label d-flex align-items-center gap-2">

          <i class="bi bi-x-circle"></i>

          Sin vuelo

        </label>

      </div>

      <!-- SOLO DIRECTOS -->
      <div v-if="!sinVuelo" class="form-check form-switch">

        <input class="form-check-input" type="checkbox" v-model="soloDirectos">

        <label class="form-check-label d-flex align-items-center gap-2">

          <i class="bi bi-signpost-2"></i>

          Solo directos

        </label>

      </div>

    </div>

    <!-- FORM -->
    <form class="row g-3" @submit.prevent="onBuscar">

      <!-- ORIGEN -->
      <div class="col-md-6">

        <label class="form-label">
          Origen
        </label>

        <select v-model="origen" class="form-select">

          <option v-for="o in origenesDisponibles" :key="o.value" :value="o.value">
            {{ o.label }}
          </option>

        </select>

      </div>

      <!-- DESTINO -->
      <div :class="destinoClass">

        <label class="form-label">
          Destino
        </label>

        <select v-model="destino" class="form-select">

          <option v-for="d in destinosFiltrados" :key="d.value" :value="d.value">

            {{ d.label }}

          </option>

        </select>

      </div>

      <!-- DIAS SIMPLE -->
      <div v-if="showDiasSimple" class="col-md-2">

        <label class="form-label">
          Días
        </label>

        <select v-model="dias" class="form-select">

          <option v-for="d in opcionesDias" :key="d" :value="d">

            {{ d }} días

          </option>

        </select>

      </div>

      <!-- COMBINADO -->
      <div v-if="showDiasCombinado" class="col-md-3">

        <div class="row">

          <div class="col-6">

            <small>
              {{ labelsCombinado[0] }}
            </small>

            <input type="number" class="form-control" v-model="dias1" :min="minDias1">

          </div>

          <div class="col-6">

            <small>
              {{ labelsCombinado[1] }}
            </small>

            <input type="number" class="form-control" v-model="dias2" :min="minDias2">

          </div>

        </div>

      </div>

      <!-- FECHA IDA -->
      <div class="col-md-3">

        <label class="form-label">
          Fecha ida
        </label>

        <FechaDatePicker v-model="fechaIda" :format-locale="es" locale="es" model-type="yyyy-MM-dd" format="yyyy-MM-dd"
          auto-apply :enable-time-picker="false" :clearable="false" :min-date="minFecha" required />

      </div>

      <!-- FECHA REGRESO -->
      <div class="col-md-3">

        <label class="form-label">
          Fecha regreso
        </label>

        <FechaDatePicker v-model="fechaRegreso" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
          format="yyyy-MM-dd" auto-apply :enable-time-picker="false" :clearable="false" :min-date="minFechaRegreso"
          :disabled="bloqueoRegreso" required />

      </div>

      <!-- TELEFONO -->
      <div :class="telefonoClass">

        <label class="form-label">
          Teléfono
        </label>

        <input type="tel" class="form-control" v-model="telefono" required>

      </div>

      <!-- VISA -->
      <div v-if="showVisa" class="col-md-3">

        <label class="form-label">
          Visa
        </label>

        <select v-model="visa" class="form-select">

          <option value="sin_visa" selected>Sin Visa</option>
          <option value="visa_americana">Visa Americana</option>
          <option value="visa_canadiense">Visa Canadiense</option>
          <option value="ambas_visas">Ambas Visas</option>

        </select>

      </div>

      <!-- PORCENTAJES / TRASLADOS -->
      <div class="col-12">
        <div class="row g-3" :class="mostrarPrecioTraslados ? 'row-cols-1 row-cols-md-3' : 'row-cols-1 row-cols-md-2'">

          <!-- Hoteles -->
          <div class="col">
            <label class="form-label">Hoteles</label>

            <div class="input-group">
              <input type="number" class="form-control" v-model="porcentajeHoteles" min="0">
              <span class="input-group-text">%</span>
            </div>
          </div>

          <!-- Vuelos -->
          <div class="col">
            <label class="form-label">Vuelos</label>

            <div class="input-group">
              <input type="number" class="form-control" v-model="porcentajeVuelos" min="0">
              <span class="input-group-text">%</span>
            </div>
          </div>

          <!-- Precio traslados -->
          <div v-if="mostrarPrecioTraslados" class="col">
            <label class="form-label">Precio traslados</label>

            <div class="input-group">
              <span class="input-group-text">COP</span>

              <input type="text" class="form-control" v-model="precioTrasladosFormateado" placeholder="0">
            </div>
          </div>

        </div>
      </div>

      <!-- HABITACIONES -->
      <HabitacionesForm :habitaciones="habitaciones" :colClass="colClass" :totalPersonas="totalPersonas"
        :agregarHabitacion="agregarHabitacion" :eliminarHabitacion="eliminarHabitacion" @update:adultos="
          setAdultos(
            $event.index,
            $event.value
          )
          " @update:ninos="
            setNinos(
              $event.index,
              $event.value
            )
            " @update:infantes="
              setInfantes(
                $event.index,
                $event.value
              )
              " />

      <!-- BOTON BUSCAR -->
      <div v-if="!sinVuelo" class="col-md-6">

        <button type="submit" class="btn btn-primary w-100" :disabled="loading || cotizando">
          {{
          cotizando
          ? "Generando cotización..."
          : loading
          ? "Buscando vuelos..."
          : "Buscar vuelos"
          }}
        </button>

      </div>

      <!-- BOTON MANUAL -->
      <div v-if="!sinVuelo" class="col-md-6">

        <button type="button" class="btn btn-success w-100" @click="handleAbrirVueloManual">

          Agregar vuelo manual

        </button>

      </div>

      <div v-if="(hayResultados || sinVuelo) && !cotizacionGenerada" class="col-12">
        <button type="button" class="btn btn-warning w-100" :disabled="cotizando || loading" @click="onCotizar">
          {{ cotizando ? 'Generando cotización...' : 'Generar cotización' }}
        </button>
      </div>

    </form>

    <!-- LOADING -->
    <div v-if="loading && !cotizando" class="text-center mt-5">

      <div class="spinner-border text-primary" style="
      width: 4rem;
      height: 4rem;
    "></div>

      <h5 class="mt-4 fw-bold">

        Buscando vuelos...

      </h5>

      <p class="text-muted mb-0">
        Esto puede tardar un momento
      </p>

    </div>

    <!-- LOADING COTIZACION -->
    <div v-if="cotizando" class="text-center mt-5">

      <div class="spinner-border text-warning" style="
      width: 4rem;
      height: 4rem;
    "></div>

      <h5 class="mt-4 fw-bold">

        Generando cotización...

      </h5>

      <p class="text-muted mb-0">

        Preparando precios y PDF

      </p>

    </div>

    <!-- RESULTADOS -->
    <ResultadoVuelos v-if="hayResultados" :resultados="resultados" />

    <!-- MODAL VUELO MANUAL -->
    <VueloManualModal :visible="modalVueloVisible" :multiDestino="multiDestino" :ida="ida" :intermedio="intermedio"
      :regreso="regreso" :precioSimple="precioSimple" :preciosMulti="preciosMulti" :fechaIda="fechaIda"
      :fechaRegreso="fechaRegreso" @close="
        modalVueloVisible = false
        " @guardar="handleGuardarVuelo" @update:multiDestino="
          multiDestino = $event
          " @update:precioSimple="
            precioSimple = $event
            " />

  </div>

</template>
